import { FC, useMemo, useState } from "react";
import SettingsIcon from '@mui/icons-material/Settings';
import DomainIcon from '@mui/icons-material/Domain';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import StorageIcon from '@mui/icons-material/Storage';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import KeyIcon from '@mui/icons-material/Key';
import ComputerIcon from '@mui/icons-material/Computer';
import { Box, Tooltip, IconButton, Menu, MenuItem, Typography, ListItemText, ListItemIcon } from "@mui/material";
import { Link as RouterLink, useLocation, matchPath } from "react-router-dom"
import { allowedSettingsMenus, getSettingsMenuDefaultRoute } from "@/authProvider/permissionMap";
import { useHasFeature } from "@/utils/useHasFeature";
import { useOdPermissions } from "@/authProvider/useOdPermissions";


const SettingsMenu: FC = () => {
  const permissions = useOdPermissions()
  const hasFeature = useHasFeature()
  const location = useLocation()
  const match = matchPath<{rest:string}>(location.pathname, { path: '/settings/:rest' })

  const { rest: activeItem } = match?.params || {}

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<any>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allowedItems = useMemo(() => allowedSettingsMenus(permissions, hasFeature), [permissions, JSON.stringify(hasFeature)])
  const {orgDefaultRoute, encryptDefaultRoute, grnDefaultRoute} = useMemo(() => {
    return {
      orgDefaultRoute:     getSettingsMenuDefaultRoute("Organization", permissions),
      encryptDefaultRoute: getSettingsMenuDefaultRoute("Encryption", permissions),
      grnDefaultRoute:     getSettingsMenuDefaultRoute("Data Access Policy", permissions),
    }
  }, [permissions])

  if (allowedItems.size === 0) { return null }

  return (
    <Box>
      <Tooltip title={"Settings"}>
        <IconButton 
          id="settings-menu-button"
          aria-controls="settings-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          size="small"
          onClick={handleClick}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="settings-menu"
        aria-labelledby="settings-menu-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ '& .MuiListItemIcon-root': { mr: 1 } }}
      >
        {allowedItems.has('Organization') &&
          <MenuItem 
            selected={"organization" === activeItem}
            component={RouterLink} 
            to={orgDefaultRoute ?? "/settings/organization/profile/edit"} 
            onClick={handleClose}
          >
            <ListItemIcon>
              <DomainIcon sx={{ color: 'primary.light' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  Organization
                </Typography>
              }
            />
          </MenuItem>}
        
        {allowedItems.has('Data Access Policy') &&
          <MenuItem
            selected={"governance" === activeItem}
            component={RouterLink} 
            to={grnDefaultRoute ?? "/settings/governance"} 
            onClick={handleClose}
          >
            <ListItemIcon>
              <AccountBalanceIcon sx={{ color: 'primary.light' }} />
            </ListItemIcon>
            <ListItemText primary={
              <Typography color="textPrimary" variant="subtitle2">
                Data Access Policy
              </Typography>} 
            />
          </MenuItem>}
        {allowedItems.has('Encryption') &&
          <MenuItem 
            selected={"encryption" === activeItem}
            component={RouterLink} 
            to={encryptDefaultRoute ?? "/settings/encryption"} 
            onClick={handleClose}
          >
            <ListItemIcon>
              <KeyIcon sx={{ color: 'primary.light' }} />
            </ListItemIcon>
            <ListItemText primary={
              <Typography color="textPrimary" variant="subtitle2">
                Encryption
              </Typography>} 
            />
          </MenuItem>}
        {allowedItems.has('Deployment') &&
          <MenuItem 
            selected={"deployment" === activeItem}
            component={RouterLink} 
            to="/settings/deployment" 
            onClick={handleClose}
          >
            <ListItemIcon>
              <StorageIcon sx={{ color: 'primary.light' }} />
            </ListItemIcon>
            <ListItemText primary={
              <Typography color="textPrimary" variant="subtitle2">
                Deployment
              </Typography>} 
            />
          </MenuItem>}

        {allowedItems.has('Notification') &&
          <MenuItem 
            selected={"notification" === activeItem}
            component={RouterLink} 
            to="/settings/notification" 
            onClick={handleClose}
          >
            <ListItemIcon>
              <NotificationsNoneIcon sx={{ color: 'primary.light' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  Alarms and Notifications
                </Typography>
              }
            />
          </MenuItem>}
        {allowedItems.has('SecureFileSettings') &&
          <MenuItem 
            selected={"securefile" === activeItem}
            component={RouterLink} 
            to="/settings/securefile" 
            onClick={handleClose}
          >
            <ListItemIcon>
              <ComputerIcon sx={{ color: 'primary.light' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  SecureFile
                </Typography>
              }
            />
          </MenuItem>}
        {allowedItems.has('SecureAISettings') &&
          <MenuItem 
            selected={"secureai" === activeItem}
            component={RouterLink} 
            to="/settings/secureai" 
            onClick={handleClose}
          >
            <ListItemIcon>
              <SmartToyIcon sx={{ color: 'primary.light' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  SecureAI
                </Typography>
              }
            />
          </MenuItem>}
      </Menu>
    </Box>
  )
}

export default SettingsMenu;
